import { useCallback } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import {
  SettingsType,
  buildPcfSettingsDtoFromStore,
  deletePipeexSettings,
  renamePipeexSettings,
  saveCurrentConfigurationToPipeex,
  setActivePipeexProfile,
  snapshotCurrentPcfSettingsStoreForDebug,
  summarizePcfSettingsDtoDataFlowForDebug,
  type SettingInfoDto,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { buildProfileKey, parseSettingsType } from '../lib/profile';
import { warnMappingFlow } from '../lib/mappingTrace';
import { useConfirmationDialog } from '../../../shared/ui/dialogs/useConfirmationDialog';
import { debugMappingProfile } from './profileControllerShared';

const LastProfileDeletionError =
  'PipeEX cannot delete every mapping profile because the workspace requires at least one profile. Save a replacement profile, then retry the deletion.';

interface ConfirmedProfileRef {
  name: string;
  settingsType: SettingsType;
  profileKey: string;
}

interface UseProfileCrudActionsArgs {
  selectedProfileKey: string;
  selectedSettingsType: SettingsType;
  lastAppliedProfileKey: string;
  profiles: SettingInfoDto[];
  selectedProfileKeyRef: MutableRefObject<string>;
  flowSnapshotPersistingRef: MutableRefObject<boolean>;
  flowSnapshotSupersededRef: MutableRefObject<boolean>;
  setSelectedProfileKey: Dispatch<SetStateAction<string>>;
  setSelectedSettingsType: Dispatch<SetStateAction<SettingsType>>;
  setLastAppliedProfileKey: Dispatch<SetStateAction<string>>;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  setSaveError: Dispatch<SetStateAction<string | null>>;
  setProfileError: Dispatch<SetStateAction<string | null>>;
  saveConfiguration: (profileName?: string, settingsType?: SettingsType) => Promise<void> | void;
  markWorkflowApplied: () => void;
  loadProfiles: () => Promise<void>;
  onProfileConfirmed?: (profile: ConfirmedProfileRef) => void;
}

/**
 * Profile CRUD actions (save, save-as, delete with destructive confirmation)
 * for the mapping profile picker. Selection/error/saving state lives in the
 * parent controller and is shared here through setters.
 */
export const useProfileCrudActions = ({
  selectedProfileKey,
  selectedSettingsType,
  lastAppliedProfileKey,
  profiles,
  selectedProfileKeyRef,
  flowSnapshotPersistingRef,
  flowSnapshotSupersededRef,
  setSelectedProfileKey,
  setSelectedSettingsType,
  setLastAppliedProfileKey,
  setIsSaving,
  setSaveError,
  setProfileError,
  saveConfiguration,
  markWorkflowApplied,
  loadProfiles,
  onProfileConfirmed,
}: UseProfileCrudActionsArgs) => {
  const { confirmDestructive, confirmationDialog: profileDeleteConfirmationDialog } =
    useConfirmationDialog();

  const onSaveProfileAs = useCallback(async (profileName: string): Promise<boolean> => {
    const trimmedProfileName = profileName.trim();
    if (!trimmedProfileName) {
      return false;
    }

    setIsSaving(true);
    setSaveError(null);
    setProfileError(null);
    try {
      if (flowSnapshotPersistingRef.current) {
        flowSnapshotSupersededRef.current = true;
      }
      const saveAsDto = buildPcfSettingsDtoFromStore(selectedSettingsType);
      debugMappingProfile('[MappingProfileController] save profile as start', {
        targetProfileName: trimmedProfileName,
        settingsType: selectedSettingsType,
        selectedProfileKey,
        lastAppliedProfileKey,
        storeSnapshot: snapshotCurrentPcfSettingsStoreForDebug(),
        dataFlow: summarizePcfSettingsDtoDataFlowForDebug(saveAsDto),
      });
      await saveCurrentConfigurationToPipeex(trimmedProfileName, selectedSettingsType);
      useConfigurationStore.getState().markClean();
      await loadProfiles();
      const nextProfileKey = buildProfileKey(trimmedProfileName, selectedSettingsType);
      setActivePipeexProfile(trimmedProfileName, selectedSettingsType);
      selectedProfileKeyRef.current = nextProfileKey;
      setSelectedProfileKey(nextProfileKey);
      setLastAppliedProfileKey(nextProfileKey);
      onProfileConfirmed?.({
        name: trimmedProfileName,
        settingsType: selectedSettingsType,
        profileKey: nextProfileKey,
      });
      debugMappingProfile('[MappingProfileController] save profile as complete', {
        profileKey: nextProfileKey,
        activeProfileName: trimmedProfileName,
        settingsType: selectedSettingsType,
      });
      return true;
    } catch (error) {
      setProfileError(error instanceof Error
        ? `PipeEX could not save the new mapping profile: ${error.message}. Review the profile name and connection, then try again.`
        : 'PipeEX could not save the new mapping profile because the service returned no error details. Check the connection, then try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    lastAppliedProfileKey,
    loadProfiles,
    onProfileConfirmed,
    selectedProfileKey,
    selectedSettingsType,
  ]);

  const onSaveProfile = useCallback(async () => {
    if (!selectedProfileKey) {
      return;
    }

    const [name, settingsTypeRaw] = selectedProfileKey.split('::');
    const settingsType = parseSettingsType(settingsTypeRaw);

    setIsSaving(true);
    setSaveError(null);
    setProfileError(null);
    try {
      await saveConfiguration(name, settingsType);
      markWorkflowApplied();
      await loadProfiles();
      const nextProfileKey = buildProfileKey(name, settingsType);
      setActivePipeexProfile(name, settingsType);
      selectedProfileKeyRef.current = nextProfileKey;
      setSelectedProfileKey(nextProfileKey);
      setSelectedSettingsType(settingsType);
      setLastAppliedProfileKey(nextProfileKey);
      onProfileConfirmed?.({
        name,
        settingsType,
        profileKey: nextProfileKey,
      });
    } catch (error) {
      setProfileError(error instanceof Error
        ? `PipeEX could not update the selected mapping profile: ${error.message}. The saved profile may be unchanged; verify the connection, then try again.`
        : 'PipeEX could not update the selected mapping profile because the service returned no error details. The saved profile may be unchanged; check the connection, then try again.');
    } finally {
      setIsSaving(false);
    }
  }, [loadProfiles, markWorkflowApplied, onProfileConfirmed, saveConfiguration, selectedProfileKey]);

  const onRenameProfile = useCallback(async (
    profileKey: string,
    profileName: string
  ): Promise<boolean> => {
    const trimmedProfileName = profileName.trim();
    if (!profileKey || !trimmedProfileName) {
      return false;
    }

    const [currentName, settingsTypeRaw] = profileKey.split('::');
    const settingsType = parseSettingsType(settingsTypeRaw);
    if (currentName === trimmedProfileName) {
      return true;
    }

    setIsSaving(true);
    setProfileError(null);
    try {
      await renamePipeexSettings(currentName, trimmedProfileName, settingsType);
      await loadProfiles();
      const nextProfileKey = buildProfileKey(trimmedProfileName, settingsType);
      if (profileKey === selectedProfileKey) {
        selectedProfileKeyRef.current = nextProfileKey;
        setSelectedProfileKey(nextProfileKey);
        setSelectedSettingsType(settingsType);
      }
      if (profileKey === lastAppliedProfileKey) {
        setLastAppliedProfileKey(nextProfileKey);
        setActivePipeexProfile(trimmedProfileName, settingsType);
        onProfileConfirmed?.({
          name: trimmedProfileName,
          settingsType,
          profileKey: nextProfileKey,
        });
      }
      return true;
    } catch (error) {
      setProfileError(error instanceof Error
        ? `PipeEX could not rename the selected mapping profile: ${error.message}. The original name remains in use; refresh profiles, then try again.`
        : 'PipeEX could not rename the selected mapping profile because the service returned no error details. The original name remains in use; refresh profiles, then try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    lastAppliedProfileKey,
    loadProfiles,
    onProfileConfirmed,
    selectedProfileKey,
    selectedProfileKeyRef,
    setIsSaving,
    setLastAppliedProfileKey,
    setProfileError,
    setSelectedProfileKey,
    setSelectedSettingsType,
  ]);

  const onDeleteProfiles = useCallback(async (profileKeys: string[]) => {
    const requestedProfileKeys = new Set(profileKeys);
    const targets = profiles.filter((profile) =>
      requestedProfileKeys.has(buildProfileKey(profile.name, profile.settingsType))
    );
    if (targets.length === 0) {
      return;
    }

    const remainingProfileCount = profiles.length - targets.length;
    if (remainingProfileCount === 0) {
      warnMappingFlow('profile delete blocked: final profiles', {
        requestedProfileCount: targets.length,
        profileCount: profiles.length,
      });
      setProfileError(LastProfileDeletionError);
      return;
    }

    const isSingleProfile = targets.length === 1;
    const confirmationMessage = isSingleProfile
      ? `Deleting “${targets[0].name}” permanently removes its saved Mapping, Quick Fill, PCF Validation, and PCF export settings. This does not remove parameters from the Revit project. Continue?`
      : `Deleting these ${targets.length} mapping profiles permanently removes their saved PipeEX settings. This does not remove parameters from the Revit project:\n\n${targets.map((profile) => profile.name).join('\n')}`;
    if (!(await confirmDestructive(
      isSingleProfile ? 'Permanently delete mapping profile?' : 'Permanently delete mapping profiles?',
      confirmationMessage,
      isSingleProfile ? 'Delete profile' : `Delete ${targets.length} profiles`,
      isSingleProfile ? 'Keep mapping profile' : 'Keep mapping profiles'
    ))) {
      return;
    }

    setIsSaving(true);
    setProfileError(null);
    try {
      const deletedProfileKeys = new Set<string>();
      const failedProfileNames: string[] = [];
      for (const profile of targets) {
        try {
          await deletePipeexSettings(profile.name, parseSettingsType(profile.settingsType));
          deletedProfileKeys.add(buildProfileKey(profile.name, profile.settingsType));
        } catch {
          failedProfileNames.push(profile.name);
        }
      }

      if (deletedProfileKeys.has(selectedProfileKey)) {
        selectedProfileKeyRef.current = '';
        setSelectedProfileKey('');
      }
      if (deletedProfileKeys.has(lastAppliedProfileKey)) {
        setLastAppliedProfileKey('');
      }

      if (deletedProfileKeys.size > 0) {
        await loadProfiles();
      }
      if (failedProfileNames.length > 0) {
        setProfileError(
          `PipeEX could not delete ${failedProfileNames.length} of ${targets.length} mapping profiles: ${failedProfileNames.join(', ')}. The other requested profiles were deleted; refresh the list, then retry the remaining profiles.`
        );
      }
    } catch (error) {
      setProfileError(error instanceof Error
        ? `The profiles were deleted, but PipeEX could not refresh the profile list: ${error.message}. Refresh profiles to confirm the current list.`
        : 'The profiles were deleted, but PipeEX could not refresh the profile list because the service returned no error details. Refresh profiles to confirm the current list.');
    } finally {
      setIsSaving(false);
    }
  }, [
    confirmDestructive,
    lastAppliedProfileKey,
    loadProfiles,
    profiles,
    selectedProfileKey,
    selectedProfileKeyRef,
    setIsSaving,
    setLastAppliedProfileKey,
    setProfileError,
    setSelectedProfileKey,
  ]);

  const onDeleteProfile = useCallback(async (profileKey?: string) => {
    const targetProfileKey = profileKey ?? selectedProfileKey;
    if (targetProfileKey) {
      await onDeleteProfiles([targetProfileKey]);
    }
  }, [onDeleteProfiles, selectedProfileKey]);

  return {
    profileDeleteConfirmationDialog,
    onSaveProfile,
    onSaveProfileAs,
    onRenameProfile,
    onDeleteProfile,
    onDeleteProfiles,
  };
};
